import { memo } from 'react';
import { Box } from '@amzn/awsui-components-core';

import styles from './CharCountIndicator.module.scss';

interface CharCountIndicatorProps {
  readonly convertedLength: number;
  readonly min: number;
  readonly max: number;
  readonly isValidationErrorVisible?: boolean;
  /** When true, the count is being recomputed — show a placeholder ("…") instead of the (stale) number. */
  readonly isCountPending?: boolean;
  /** When true, an empty value is valid — the range applies only once the user enters text. */
  readonly isOptional?: boolean;
}

/** Shows character count with range requirement. Highlights the count in red when out of range. */
const CharCountIndicator = memo(function CharCountIndicator({
  convertedLength,
  min,
  max,
  isValidationErrorVisible = false,
  isCountPending = false,
  isOptional = false,
}: CharCountIndicatorProps) {
  // An optional field with no input is valid, so it's never out of range at zero length.
  const isEmptyOptional = isOptional && convertedLength === 0;
  const isOutOfRange = !isEmptyOptional && (convertedLength < min || convertedLength > max);
  const noun = convertedLength === 1 ? 'character' : 'characters';
  const color = isOutOfRange && !isCountPending ? 'text-status-error' : 'text-body-secondary';

  return (
    <div className={styles.charCountRow}>
      <Box
        variant="p"
        fontSize="body-s"
        color={isValidationErrorVisible ? 'text-status-error' : 'text-body-secondary'}
      >
        {isValidationErrorVisible ? 'Invalid title' : `${min} - ${max} characters required`}
      </Box>
      <Box variant="p" fontSize="body-s" color={color}>
        {isCountPending ? '…' : `${convertedLength} ${noun}`}
      </Box>
    </div>
  );
});

export default CharCountIndicator;
